import { type RefObject, useLayoutEffect } from "react";

/** 横スクロールを維持しつつ、元の列見出しをページの操作ヘッダー直下に留める。 */
export function usePageStickyHeader(
  ref: RefObject<HTMLTableSectionElement | null>,
) {
  // thead 自体が置き換わる場合は TableHeader が再マウントされるため、通常の再描画では再接続しない。
  useLayoutEffect(() => {
    const head = ref.current;
    const main = head?.closest("main");
    const table = head?.closest("table");
    if (
      !head ||
      !main ||
      !table ||
      !main.hasAttribute("data-page-sticky-tables")
    )
      return;
    let frame = 0;
    const control = main.querySelector<HTMLElement>("[data-workspace-control]");
    // 横スクロール用の overflow 祖先が sticky の基準になるため、thead は外側の main へ native sticky できない。
    // Scroll-driven Animations は main の描画と同期できるが未対応環境があるため、rAF fallback を残す。
    // https://developer.mozilla.org/docs/Web/CSS/position#sticky
    // https://developer.mozilla.org/docs/Web/CSS/animation-timeline#browser_compatibility
    const scrollDriven =
      CSS.supports("animation-timeline: --analytics-page-scroll") &&
      CSS.supports("animation-range: 0px 1px");
    const update = () => {
      frame = 0;
      const mainTop = main.getBoundingClientRect().top;
      const top =
        mainTop +
        (control?.hasAttribute("data-sticky") ? control.offsetHeight : 0);
      // 元のレイアウト位置から測り、ソートボタン・列幅・横位置もそのまま保つ。
      const originalTop = table.getBoundingClientRect().top + head.offsetTop;
      const distance = Math.max(
        0,
        table.offsetHeight - head.offsetHeight - head.offsetTop,
      );
      if (scrollDriven) {
        const start = main.scrollTop + originalTop - top;
        head.style.setProperty("--page-sticky-start", `${start}px`);
        head.style.setProperty(
          "--page-sticky-end",
          `${start + Math.max(1, distance)}px`,
        );
        head.style.setProperty("--page-sticky-distance", `${distance}px`);
        return;
      }
      const offset = Math.max(0, Math.min(top - originalTop, distance));
      head.style.transform = `translateY(${offset}px)`;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    head.classList.add("page-sticky-table-header", "print:transform-none!");
    head.style.position = "relative";
    head.style.zIndex = "20";
    head.style.background = "var(--background)";
    head.style.willChange = "transform";
    update();
    if (!scrollDriven)
      main.addEventListener("scroll", schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    for (
      let element: Element | null = table;
      element && element !== main;
      element = element.parentElement
    ) {
      observer.observe(element, { box: "border-box" });
    }
    observer.observe(main, { box: "border-box" });
    if (control) observer.observe(control, { box: "border-box" });
    const mutationObserver = control
      ? new MutationObserver(schedule)
      : undefined;
    if (control)
      mutationObserver?.observe(control, {
        attributes: true,
        attributeFilter: ["data-sticky"],
      });
    return () => {
      main.removeEventListener("scroll", schedule);
      observer.disconnect();
      mutationObserver?.disconnect();
      cancelAnimationFrame(frame);
      head.classList.remove(
        "page-sticky-table-header",
        "print:transform-none!",
      );
      head.style.removeProperty("transform");
      head.style.removeProperty("--page-sticky-start");
      head.style.removeProperty("--page-sticky-end");
      head.style.removeProperty("--page-sticky-distance");
      head.style.removeProperty("position");
      head.style.removeProperty("z-index");
      head.style.removeProperty("background");
      head.style.removeProperty("will-change");
    };
  }, [ref]);
}
